import React, { useState, useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { CustomPopup } from '../../../common/Popup/CustomPopup'
import MiniPopup from '../../../common/Popup/MiniPopup'
import {
	DETAILS_APPLY_FOR,
	SHIPPING_TYPE
} from '../../../constants'
import SessionKeys from '../../../constants/sessionKeys'
import { getFromSessionStorage, getBase64Decoded, setToSessionStorage, getBase64Encoded } from '../../../utils/StorageUtils'
import { calculateDimensionToCm, calculateWeightToKg, round } from '../../../utils/common'
import { useDispatch, useSelector } from 'react-redux'
import { setShipmentClassification } from '../../../redux/actions/shipmentClassificationActions'
import { getShipmentClassificationSignature } from '../../../utils/shipmentClassification'
import { setSearchFormData } from '../../../redux/actions/searchFormActions'
import { PrimaryButton, SecondaryButton } from '../../../common/Buttons'
import plusIcon from '../../../assets/images/plusBlue.svg'
import uploadIconBlack from '../../../assets/images/uploadIconBlack.svg'
import errorFilledOrange from '../../../assets/images/errorFilledOrange.svg'
import SlrPackagingRow from './SlrPackagingRow'
import SearchSummary from '../../SearchSummary/SearchSummary'
import CommonInput from '../../../common/Resusable/CommonInput'
import { submitSlrBooking, classifyShipment, submitSlrCargoStack, saveSlrAttachment } from '../../../services/slrService'
import { CustomToast } from '../../../common/CustomToast/CustomToast'
import { AlertMessage } from '../../SearchPanel/AlertMessage'
import MESSAGES from '../../../constants/Messages'
import getCurrentUser from '../../../utils/getCurrentUser'
import { sendSlrNotificationEmails } from '../SLRUtils'
import LoadingIndicator from '../../LoadingIndicator'
import RadioToggle from '../../SearchPanel/RadioToggle'
import './SlrBooking.scss'

const SlrBooking = () => {
	const { t } = useTranslation(['labels', 'errors'])
	const history = useHistory()
	const dispatch = useDispatch()
	const shipmentClassification = useSelector(state => state.shipmentClassification)

	const [origin, setOrigin] = useState(null)
	const [destination, setDestination] = useState(null)
	const [shippingDate, setShippingDate] = useState(null)

	// Cargo details (pre-populated from session, disabled)
	const [shippingItem, setShippingItem] = useState(null)
	const [sccCodes, setSccCodes] = useState([])


	// Packaging rows state (one per looseDetails item from search)
	const [packagingRows, setPackagingRows] = useState([{
		pieces: '',
		length: '',
		width: '',
		height: '',
		dimensionUnit: 'C',
		weight: '',
		weightUnit: 'K',
		weightType: DETAILS_APPLY_FOR.PER_ITEM,
		stackable: true,
		turnable: false,
		nonCraneLiftable: false,
		nonForkLiftable: false,
		fragile: false,
		packingType: 'Select',
	}])

	// Booking remarks
	const [bookingRemarks, setBookingRemarks] = useState('')

	// Airway bill number
	const [airwayBillNumber, setAirwayBillNumber] = useState('')

	// Has attachments flag
	const [hasAttachments, setHasAttachments] = useState(false)

	// Additional cargo properties (editable)
	const [isStackable, setIsStackable] = useState(null)
	const [isTiltable, setIsTiltable] = useState(null)
	const [isCraneLiftable, setIsCraneLiftable] = useState(null)
	const [isForkLiftable, setIsForkLiftable] = useState(null)
	const [isFragile, setIsFragile] = useState(null)

	// Total weight (editable)
	const [totalWeight, setTotalWeight] = useState('')
	const [weightUnit, setWeightUnit] = useState('K')

	// Weight summary (read-only, from session)
	const [weightSummary, setWeightSummary] = useState(null)

	// Result popup
	const [showResultPopup, setShowResultPopup] = useState(false)
	const [resultData, setResultData] = useState(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [processErrorMessage, setProcessErrorMessage] = useState(null)

	// Disqualify popup (shown when edited dimensions no longer meet SLR criteria)
	const [showDisqualifyPopup, setShowDisqualifyPopup] = useState(false)

	// Attachments
	const [attachments, setAttachments] = useState([])
	const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
	const attachmentUploadRef = useRef(false)
	const [notification, setNotification] = useState(null)

	// Validation errors
	const [additionalPropsError, setAdditionalPropsError] = useState('')
	const [packagingRowsErrors, setPackagingRowsErrors] = useState([])
	const [attachmentError, setAttachmentError] = useState('')

	useEffect(() => {
		window.scrollTo(0, 0)

		const encodedSearchData = getFromSessionStorage(SessionKeys.SEARCH_FORM_DATA_BOOKING)

		// Redirect to booking/search if no valid search data exists
		if (!encodedSearchData) {
			history.push('/booking/search')
			return
		}

		const searchData = getBase64Decoded(encodedSearchData)

		// Validate that essential search data exists
		if (!searchData || !searchData.origin || !searchData.destination) {
			history.push('/booking/search')
			return
		}

		if (searchData?.origin) setOrigin(searchData.origin)
		if (searchData?.destination) setDestination(searchData.destination)
		if (searchData?.shippingDate) setShippingDate(searchData.shippingDate)
		if (searchData?.shippingItemCode) setShippingItem(searchData.shippingItemCode)
		if (searchData?.sccCodes) setSccCodes(searchData.sccCodes)
		if (searchData?.totalWeight) setTotalWeight(String(searchData.totalWeight))
		if (searchData?.weightUnit) setWeightUnit(searchData.weightUnit)
		if (searchData?.isAllStackable !== undefined) setIsStackable(searchData.isAllStackable)
		if (searchData?.isAllTiltable !== undefined) setIsTiltable(searchData.isAllTiltable)

		// Pre-populate packaging rows from looseDetails
		if (searchData?.looseDetails?.length) {
			const displayWeightUnit = searchData.weightUnit || 'K'
			const displayDimUnit = searchData.dimensionUnit || 'C'
			const totalWeightInKg = parseFloat(searchData.totalWeightInKg) || 0

			// Step 1: Verify that every row has an explicit weight value.
			// If any row is missing a weight we cannot trust the per-row data.
			const allHaveWeight = searchData.looseDetails.every((item) => {
				const w = parseFloat(item.weight)
				return Number.isFinite(w) && w > 0
			})

			// Step 2: When all rows have weights, compute their claimed total in KG
			// and compare with the authoritative totalWeightInKg.
			let usePerRowWeights = false
			if (allHaveWeight && totalWeightInKg > 0) {
				const claimedTotalInKg = searchData.looseDetails.reduce((sum, item) => {
					const w = parseFloat(item.weight)
					const pieces = Math.max(parseFloat(item.pieces) || 1, 1)
					const wInKg = parseFloat(calculateWeightToKg(w, displayWeightUnit)) || 0
					// PER_ITEM: wInKg is per-piece -> row total = wInKg * pieces
					// TOTAL: wInKg is already the row total
					return sum + (item.weightType === DETAILS_APPLY_FOR.PER_ITEM ? wInKg * pieces : wInKg)
				}, 0)
				// Allow a small floating-point tolerance (0.01 kg)
				usePerRowWeights = Math.abs(claimedTotalInKg - totalWeightInKg) <= 0.01
			}

			let rows
			if (usePerRowWeights) {
				// Totals match - use per-row weights, normalized to per-piece for the SLR field.
				// PER_ITEM: value is already per-piece, use as-is.
				// TOTAL: divide by pieces to get per-piece.
				rows = searchData.looseDetails.map((item) => {
					const w = parseFloat(item.weight)
					const pieces = Math.max(parseFloat(item.pieces) || 1, 1)
					const perPieceInDisplayUnit = item.weightType === DETAILS_APPLY_FOR.PER_ITEM
						? String(Number(w.toFixed(2)))
						: String(Number((w / pieces).toFixed(2)))
					return {
						pieces: item.pieces || '',
						length: item.length || '',
						width: item.width || '',
						height: item.height || '',
						dimensionUnit: item.dimensionUnit || displayDimUnit,
						weight: perPieceInDisplayUnit,
						weightUnit: displayWeightUnit,
						weightType: DETAILS_APPLY_FOR.PER_ITEM,
						stackable: item.stackable !== false,
						turnable: item.turnable === true,
						craneLiftable: item.craneLiftable != null ? (item.craneLiftable === true || item.craneLiftable === 'Y') : null,
						forkLiftable: item.forkLiftable != null ? (item.forkLiftable === true || item.forkLiftable === 'Y') : null,
						fragile: item.fragile === true,
						packingType: 'Select',
					}
				})
			} else {
				// Fallback: totals do not match or some rows lack weight.
				// Distribute total weight evenly: totalWeight / totalPieces per piece.
				const totalWeight = parseFloat(searchData.totalWeight || searchData.calculatedTotalWeight) || 0
				const totalPieces = parseFloat(searchData.totalPieces) || 0
				const perItemWeight = (totalWeight && totalPieces)
					? Number((totalWeight / totalPieces).toFixed(2))
					: ''
				rows = searchData.looseDetails.map((item) => ({
					pieces: item.pieces || '',
					length: item.length || '',
					width: item.width || '',
					height: item.height || '',
					dimensionUnit: item.dimensionUnit || displayDimUnit,
					weight: perItemWeight !== '' ? String(perItemWeight) : '',
					weightUnit: displayWeightUnit,
					weightType: DETAILS_APPLY_FOR.PER_ITEM,
					stackable: item.stackable !== false,
					turnable: item.turnable === true,
					craneLiftable: item.craneLiftable != null ? (item.craneLiftable === true || item.craneLiftable === 'Y') : null,
					forkLiftable: item.forkLiftable != null ? (item.forkLiftable === true || item.forkLiftable === 'Y') : null,
					fragile: item.fragile === true,
					packingType: 'Select',
				}))
			}
			setPackagingRows(rows)
		}

		// Weight summary for read-only display
		setWeightSummary({
			shippingType: searchData.shippingType || SHIPPING_TYPE.LOOSE,
			weightUnit: searchData.weightUnit || 'K',
			dimensionUnit: searchData.dimensionUnit,
			totalPieces: searchData.totalPieces || 0,
			totalVolumeInMtrs: searchData.totalVolumeInMtrs || 0,
			totalWeightInKg: searchData.totalWeightInKg || 0,
			looseDetails: searchData.looseDetails || [],
			chargeableWeight: searchData.chargeableWeight || 0,
			calculatedTotalWeight: searchData.calculatedTotalWeight || 0,
			actualGrossWeight: searchData.actualGrossWeight || 0,
			actualTotalIneItemWeight: searchData.actualTotalIneItemWeight || 0,
			actualVolume: searchData.actualVolume || 0,
			chargeableWeightInSelectedUnit: searchData.chargeableWeightInSelectedUnit || 0,
		})
	}, [history])

	const handleBack = () => {
		handleDisqualifyConfirm()
	}

	const handleAddPackagingRow = () => {
		setPackagingRows((prev) => [
			...prev,
			{
				pieces: '',
				length: '',
				width: '',
				height: '',
				dimensionUnit: 'C',
				weight: '',
				weightUnit: 'K',
				weightType: DETAILS_APPLY_FOR.PER_ITEM,
				stackable: isStackable === true,
				turnable: isTiltable === true,
				craneLiftable: isCraneLiftable,
				forkLiftable: isForkLiftable,
				fragile: isFragile === true,
				packingType: 'Select',
			},
		])
	}

	const handleRemovePackagingRow = (index) => {
		setPackagingRows((prev) => prev.filter((_, i) => i !== index))
		setPackagingRowsErrors((prev) => prev.filter((_, i) => i !== index))
	}

	const validateForm = () => {
		let isValid = true

		// Clear previous errors
		setAdditionalPropsError('')
		setPackagingRowsErrors([])
		setAttachmentError('')

		// Validate additional cargo properties (crane, fork, fragile)
		if (isCraneLiftable === null || isForkLiftable === null || isFragile === null) {
			setAdditionalPropsError('ER298')
			isValid = false
		}

		// Validate packaging rows
		const rowErrors = packagingRows.map((row) => {
			const errors = {}

			if (!row.pieces || row.pieces === '' || Number(row.pieces) === 0) {
				errors.pieces = 'ER005'
				isValid = false
			}
			if (!row.length || row.length === '' || Number(row.length) === 0) {
				errors.length = 'ER006'
				isValid = false
			}
			if (!row.width || row.width === '' || Number(row.width) === 0) {
				errors.width = 'ER007'
				isValid = false
			}
			if (!row.height || row.height === '' || Number(row.height) === 0) {
				errors.height = 'ER008'
				isValid = false
			}
			if (!row.weight || row.weight === '' || Number(row.weight) === 0) {
				errors.weight = 'ER009'
				isValid = false
			}
			if (!row.packingType || row.packingType === '' || row.packingType === 'Select') {
				errors.packingType = 'ER299'
				isValid = false
			}

			return errors
		})

		setPackagingRowsErrors(rowErrors)

		// Validate attachments if Yes is selected
		if (hasAttachments && attachments.length === 0) {
			setAttachmentError('ER236')
			isValid = false
		}

		return isValid
	}


	const handleDisqualifyConfirm = () => {
		setShowDisqualifyPopup(false)

		const encodedSearchData = getFromSessionStorage(SessionKeys.SEARCH_FORM_DATA_BOOKING)
		if (encodedSearchData) {
			const searchData = getBase64Decoded(encodedSearchData)
			const originalLooseDetails = searchData.looseDetails || []

			// Build updated looseDetails, preserving non-dimension fields from originals.
			// row.weight is per-piece (PER_ITEM) - write it back as-is for PER_ITEM,
			// or multiply by pieces for TOTAL.
			const updatedLooseDetails = packagingRows.map((row, idx) => {
				const original = originalLooseDetails[idx] || {}
				const pieces = Math.max(parseFloat(row.pieces) || 1, 1)
				const rowWeight = parseFloat(row.weight) || 0
				// looseDetails.weight: PER_ITEM -> per-piece value, TOTAL -> total for the row
				const itemWeight = row.weightType === DETAILS_APPLY_FOR.PER_ITEM
					? Number(rowWeight.toFixed(2))
					: Number((rowWeight * pieces).toFixed(2))

				return {
					...original,
					pieces: row.pieces,
					length: row.length,
					width: row.width,
					height: row.height,
					dimensionUnit: row.dimensionUnit || searchData.dimensionUnit || 'C',
					weightType: row.weightType || DETAILS_APPLY_FOR.PER_ITEM,
					weight: String(itemWeight),
					weightUnit: row.weightUnit || searchData.weightUnit || 'K',
					stackable: row.stackable !== false,
					turnable: row.turnable === true,
					craneLiftable: row.craneLiftable === true,
					forkLiftable: row.forkLiftable === true,
					fragile: row.fragile === true,
				}
			})

			// Recalculate aggregate weight fields.
			// row.weight is per-piece (PER_ITEM) -> row total = weight * pieces
			// row.weight is total (TOTAL) -> row total = weight
			const updatedTotalWeight = packagingRows.reduce((sum, row) => {
				const w = parseFloat(row.weight) || 0
				const p = parseFloat(row.pieces) || 0
				return sum + (row.weightType === DETAILS_APPLY_FOR.PER_ITEM ? w * p : w)
			}, 0)
			const updatedTotalWeightInKg = packagingRows.reduce((sum, row) => {
				const w = parseFloat(row.weight) || 0
				const p = parseFloat(row.pieces) || 0
				const wInKg = parseFloat(calculateWeightToKg(w, row.weightUnit || 'K')) || 0
				return sum + (row.weightType === DETAILS_APPLY_FOR.PER_ITEM ? wInKg * p : wInKg)
			}, 0)
			const updatedTotalPieces = packagingRows.reduce(
				(sum, row) => sum + (Number(row.pieces) || 0), 0
			)

			const updatedSearchData = {
				...searchData,
				looseDetails: updatedLooseDetails,
				totalWeight: updatedTotalWeight,
				totalWeightInKg: updatedTotalWeightInKg,
				calculatedTotalWeight: updatedTotalWeight,
				actualGrossWeight: updatedTotalWeight,
				actualTotalIneItemWeight: updatedTotalWeight,
				grossWeightInSelectedUnit: updatedTotalWeight,
				grossWeightInConvertedUnit: round(updatedTotalWeightInKg, 2),
				totalPieces: updatedTotalPieces,
			}

			setToSessionStorage(SessionKeys.SEARCH_FORM_DATA_BOOKING, getBase64Encoded(updatedSearchData))
			dispatch(setSearchFormData(updatedSearchData))
		}

		history.push('/booking/search')
	}

	const handleDisqualifyCancel = () => {
		setShowDisqualifyPopup(false)
	}


	const handleConfirm = async () => {
		if (isSubmitting || attachmentUploadRef.current) return
		// Validate form first
		if (!validateForm()) {
			window.scrollTo(0, 0)
			return
		}

		setIsSubmitting(true)
		setProcessErrorMessage(null)
		try {
			const classificationOptions = { operationFlag: 'I', totalWeight, weightUnit }
			const signature = getShipmentClassificationSignature(packagingRows, classificationOptions)
			const classificationResult = shipmentClassification?.signature === signature && shipmentClassification?.result?.type
				? shipmentClassification.result
				: await classifyShipment(packagingRows, getCurrentUser()?.agentCode, classificationOptions)
			if (classificationResult?.type) {
				dispatch(setShipmentClassification(signature, classificationResult))
			}
			if (!classificationResult?.type) {
				setProcessErrorMessage(t(MESSAGES.PROCESS_REQUEST_FAILED, { ns: 'errors' }))
				setIsSubmitting(false)
				return
			}
			if (classificationResult.type !== 'SLR') {
				setShowDisqualifyPopup(true)
				setIsSubmitting(false)
				return
			}
		} catch (error) {
			setProcessErrorMessage(t(MESSAGES.PROCESS_REQUEST_FAILED, { ns: 'errors' }))
			setIsSubmitting(false)
			return
		}

		try {
			const shipment = {
				origin,
				destination,
				shippingDate,
				shippingItem,
				sccCodes,
				totalWeight,
				weightUnit,
				packagingRows,
				shippingDescription: bookingRemarks,
				airwayBillNumber,
				isStackable,
				isTiltable,
				isCraneLiftable,
				isForkLiftable,
				isFragile,
				attachments,
			}
			const response = await submitSlrBooking(shipment, false)
			if (response?.status === 200) {
				setResultData({
					success: true,
					slrNumber: response.data?.slrReferenceNumber,
				})
				setShowResultPopup(true)

				submitSlrCargoStack(response.data, shipment).catch(error => {
					console.error('[SlrBooking] Background CargoStack request failed:', error)
				})

				// === TRIGGER EMAIL NOTIFICATIONS ===
				const userDetails = getCurrentUser()
				const emailConfig = {
					agentName: userDetails?.companyName,
					agentPhone: userDetails?.telephoneNo || userDetails?.mobileNo || '',
					agentEmail: userDetails?.email,
					customerEmail: userDetails?.email,
					customerDetails: `${userDetails?.userName ? `${userDetails?.userName} / ` : ''}${userDetails?.agentCode}`,
					agentCode: userDetails?.agentCode,
					shippingDescription: bookingRemarks,
				}
				sendSlrNotificationEmails(response, packagingRows, shippingDate, sccCodes.map(e => e.scc), userDetails, emailConfig)
					.catch(error => {
						console.error('[SlrBooking] Background email notification failed:', error)
					})
			} else {
				setResultData({ success: false })
				setShowResultPopup(true)
			}
		} catch (error) {
			setResultData({
				success: false,
				errorMessage: error?.response?.data?.errorDescription,
			})
			setShowResultPopup(true)
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleResultOk = () => {
		setShowResultPopup(false)
		if (resultData?.success) {
			history.push('/slr-bookings', { fromSlrBookingResult: true })
		}
	}

	const onDrop = async (acceptedFiles, fileRejections = []) => {
		if (attachmentUploadRef.current || isSubmitting || (acceptedFiles.length === 0 && fileRejections.length === 0)) return
		attachmentUploadRef.current = true
		setIsUploadingAttachments(true)
		setAttachmentError('')
		const uploadedFiles = []
		const failedFiles = fileRejections.map(() => t('COMMON.FILE_SIZE_LIMIT', { maxSize: 2 }))
		try {
			for (const file of acceptedFiles) {
				if (!/\.(pdf|doc|docx|xls|xlsx|xlx|jpeg|jpg|png|txt)$/i.test(file.name)) {
					failedFiles.push(t('COMMON.FILE_INVALID_FORMAT', { fileName: file.name }))
					continue
				}
				if ([...attachments, ...uploadedFiles].some(attachment =>
					(attachment.fileName || attachment.name) === file.name)) {
					failedFiles.push(t('COMMON.FILE_ALREADY_EXISTS', { fileName: file.name }))
					continue
				}
				try {
					const uploaded = await saveSlrAttachment(file, uuidv4())
					uploadedFiles.push({ ...uploaded, name: uploaded.fileName || file.name })
				} catch (error) {
					failedFiles.push(t('COMMON.FILE_UPLOAD_FAILED', { fileName: file.name }))
				}
			}
			if (uploadedFiles.length > 0) {
				setAttachments(prev => [...prev, ...uploadedFiles])
			}
			if (failedFiles.length > 0) {
				setNotification({ severity: 'error', summary: t('COMMON.ERROR'), detail: [...new Set(failedFiles)].join('; ') })
			} else if (uploadedFiles.length > 0) {
				setNotification({ severity: 'success', summary: t('COMMON.SUCCESS'), detail: t('COMMON.FILE_UPLOAD_SUCCESS') })
			}
		} finally {
			attachmentUploadRef.current = false
			setIsUploadingAttachments(false)
		}
	}

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		multiple: true,
		maxSize: 2 * 1024 * 1024,
		disabled: isUploadingAttachments || isSubmitting,
	})

	const handleRemoveAttachment = (index) => {
		setAttachments((prev) => prev.filter((_, i) => i !== index))
	}

	return (
		<div className="search-panel slr-search-panel w-100">
			<h3 className="slr-main-title">{t('BOOKING.SLR_BOOKING_TITLE')}</h3>



			{/* Cargo Details Section */}
			<div className="new-booking-container">
				<div className="form-container-shippingdetails">
					<h3>{t('BOOKING.SLR_CARGO_DETAILS')}</h3>
					<div className="form-container">

						{/* Specify if applicable - editable properties */}
						<div className="cargo-pieceschkbox-row position-relative slr-specify-radio-section">
							<label className="sub-head-label mb-0">{t('BOOKING.SLR_SPECIFY_IF_APPLICABLE')}</label>
							<div className="slr-inline-radio-group">
								<div className="slr-inline-radio-item">
									<label className="slr-radio-label">{t('COMMON.STACKABLE')}</label>

									<RadioToggle
										checkedArray={[isStackable === true, isStackable === false]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className="slr-stackable-toggle"
										onChangeArray={[
											() => {
												setIsStackable(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, stackable: true })))
											},
											() => {
												setIsStackable(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, stackable: false })))
											}
										]}
									/>
								</div>
								<div className="slr-inline-radio-item">
									<label className="slr-radio-label">{t('COMMON.TILTABLE')}</label>

									<RadioToggle
										checkedArray={[isTiltable === true, isTiltable === false]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className="slr-tiltable-toggle"
										onChangeArray={[
											() => {
												setIsTiltable(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, turnable: true })))
											},
											() => {
												setIsTiltable(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, turnable: false })))
											}
										]}
									/>
								</div>
								<div className="slr-inline-radio-item">
									<label className="slr-radio-label">{t('BOOKING.SLR_CRANE_LIFTABLE')}</label>
									<RadioToggle
										checkedArray={[isCraneLiftable === true, isCraneLiftable === false]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className="slr-crane-toggle"
										onChangeArray={[
											() => {
												setIsCraneLiftable(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, craneLiftable: true })))
											},
											() => {
												setIsCraneLiftable(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, craneLiftable: false })))
											}
										]}
									/>
								</div>
								<div className="slr-inline-radio-item">
									<label className="slr-radio-label">{t('BOOKING.SLR_FORK_LIFTABLE')}</label>

									<RadioToggle
										checkedArray={[isForkLiftable === true, isForkLiftable === false]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className="slr-fork-toggle"
										onChangeArray={[
											() => {
												setIsForkLiftable(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, forkLiftable: true })))
											},
											() => {
												setIsForkLiftable(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, forkLiftable: false })))
											}
										]}
									/>
								</div>
								<div className="slr-inline-radio-item">
									<label className="slr-radio-label">{t('BOOKING.SLR_FRAGILE')}</label>

									<RadioToggle
										checkedArray={[isFragile === true, isFragile === false]}
										valueArray={['Yes', 'No']}
										labelArray={['COMMON.YES', 'COMMON.NO']}
										className="slr-fragile-toggle"
										onChangeArray={[
											() => {
												setIsFragile(true)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, fragile: true })))
											},
											() => {
												setIsFragile(false)
												setAdditionalPropsError('')
												setPackagingRows((prev) => prev.map((r) => ({ ...r, fragile: false })))
											}
										]}
									/>
								</div>
							</div>
							{additionalPropsError && (
								<span className="common-input-error">{t(additionalPropsError, { ns: 'errors' })}</span>
							)}
						</div>

						{/* Packaging Details - verify shipment details */}
						<div className="cargo-pieceschkbox-row slr-packaging-details-section">
							<label className="sub-head-label mb-0">{t('BOOKING.SLR_VERIFY_SHIPMENT_DETAILS')}</label>
							<div className="slr-packaging-rows">
								{packagingRows.map((row, idx) => (
									<SlrPackagingRow
										key={idx}
										row={row} rowIndex={idx} canRemove={packagingRows.length > 1}
										isStackable={isStackable}
										isTiltable={isTiltable} isCraneLiftable={isCraneLiftable}
										isForkLiftable={isForkLiftable}
										isFragile={isFragile}
										onRemove={() => handleRemovePackagingRow(idx)}
										onChange={(field, value) => {
											setPackagingRows((prev) => {
												const updated = [...prev]
												updated[idx] = { ...updated[idx], [field]: value }
												// Rows -> Radio sync
												if (field === 'craneLiftable') {
													const allCraneLiftable = updated.every((r) => r.craneLiftable === true)
													setIsCraneLiftable(allCraneLiftable ? true : false)
												}
												if (field === 'forkLiftable') {
													const allForkLiftable = updated.every((r) => r.forkLiftable === true)
													setIsForkLiftable(allForkLiftable ? true : false)
												}
												if (field === 'fragile') {
													const allFragile = updated.every((r) => r.fragile === true)
													setIsFragile(allFragile ? true : false)
												}
												if (field === 'stackable') {
													const allStackable = updated.every((r) => r.stackable === true)
													setIsStackable(allStackable ? true : false)
												}
												if (field === 'turnable') {
													const allTiltable = updated.every((r) => r.turnable === true)
													setIsTiltable(allTiltable ? true : false)
												}
												return updated
											})
											// Clear error for this field
											if (packagingRowsErrors[idx]) {
												setPackagingRowsErrors((prev) => {
													const updated = [...prev]
													if (updated[idx]) {
														updated[idx] = { ...updated[idx], [field]: '' }
													}
													return updated
												})
											}
										}}
										errors={packagingRowsErrors[idx] || {}}
									/>
								))}
							</div>
							<div className="cargo-addupload-row mb-0">
								<div className="add-more-btn" onClick={handleAddPackagingRow}>
									<img className='add-more-plus' src={plusIcon} alt='plus' title='add-more-plus' />
									<a className="add-more-link">
										<span>{`${t('COMMON.ADD_ANOTHER')}`}</span>
									</a>
								</div>
							</div>
						</div>

						<div className='cargo-pieceschkbox-row'>
							<div className="form-group slr-remarks-input">
								<label className="sub-head-label mb-2">{t('BOOKING.SLR_BOOKING_REMARKS_PLACEHOLDER')}</label>
								<textarea
									className='form-control slr-remarks-textarea'
									value={bookingRemarks}
									onChange={(e) => setBookingRemarks(e.target.value)}
									rows={2}
								/>
							</div>
						</div>

						{/* Has Attachments Radio Button */}
						<div className='cargo-pieceschkbox-row position-relative'>
							<div className='form-group'>
								<label className="sub-head-label mb-2">{t('BOOKING.SLR_HAS_ATTACHMENTS')}</label>
								<RadioToggle
									checkedArray={[hasAttachments, !hasAttachments]}
									valueArray={['Yes', 'No']}
									labelArray={['COMMON.YES', 'COMMON.NO']}
									className="has-attachments-toggle"
									onChangeArray={[
										() => { if (!isUploadingAttachments && !isSubmitting) setHasAttachments(true) },
										() => {
											if (isUploadingAttachments || isSubmitting) return
											setHasAttachments(false)
											setAttachments([])
											setAttachmentError('')
										}
									]}
								/>
							</div>
						</div>



						{/* Row 5: Attachments */}
						{hasAttachments && (
							<div className="cargo-pieceschkbox-row position-relative slr-file-upload">
								<label className="sub-head-label mb-0">{t('BOOKING.SLR_ATTACHMENTS')}</label>
								{attachments.length > 0 && (
									<div className="dg-upload-body w-100">
										<ul className="dg-up-file-list">
											{attachments.map((file, i) => (
												<li key={i} className="dg-up-file">
													<div className="up-file-details">
														{file.url ? (
															<a href={file.url} target='_blank' rel='noopener noreferrer' className='slr-attachment-link'>
																{file.fileName || file.name}
															</a>
														) : <span>{file.fileName || file.name}</span>}
													</div>
													<span
														className="up-file-delete"
														onClick={() => { if (!isUploadingAttachments && !isSubmitting) handleRemoveAttachment(i) }}
													>
														{t('COMMON.DELETE')}
													</span>
												</li>
											))}
										</ul>
									</div>
								)}
								<div className="file-upload-block">
									<div className="upload-content-block" {...getRootProps()}>
										<input {...getInputProps()} />
										<img src={uploadIconBlack} alt="upload" title="upload" />
										<div className="upload-drop-label">
											<label className="upload-label">{t('COMMON.UPLOAD_CLICK')}</label>
											<label className="drop-label">{t('COMMON.UPLOAD_DROP')}</label>
										</div>
										<label className="drop-label">{t('COMMON.UPLOAD_TYPE_INFO')}</label>
									</div>
								</div>
								<span className="common-input-error">
									{attachmentError ? t(attachmentError, { ns: 'errors' }) : ''}
								</span>
							</div>
						)}
						{/* Airway Bill Number */}
						<div className='cargo-pieceschkbox-row'>
							<div className='form-group slr-remarks-input'>
								<CommonInput
									type='text'
									label={t('BOOKING.SLR_AIRWAY_BILL_PLACEHOLDER')}
									value={airwayBillNumber}
									onChange={(e) => setAirwayBillNumber(e.target.value)}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>



			{/* Weight Details Section */}
			{weightSummary && (
				<div className="new-booking-container">
					<SearchSummary
						totalPieces={weightSummary.totalPieces}
						weightUnit={weightSummary.weightUnit}
						dimensionUnit={weightSummary.dimensionUnit}
						totalWeightInKg={weightSummary.totalWeightInKg}
						totalVolumeInMtrs={weightSummary.totalVolumeInMtrs}
						chargeableWeight={weightSummary.chargeableWeight}
						looseDetails={weightSummary.looseDetails}
						shippingType={weightSummary.shippingType}
						totalWeight={null}
						looseItemsGrossWeight={weightSummary.actualGrossWeight}
						calculatedTotalWeight={weightSummary.calculatedTotalWeight}
						actualGrossWeight={weightSummary.actualGrossWeight}
						actualTotalIneItemWeight={weightSummary.actualTotalIneItemWeight}
						actualVolume={weightSummary.actualVolume}
						setGrossWeightInSelectedUnit={() => { }}
						setGrossWeightInConvertedUnit={() => { }}
						setDensityInSelectedUnit={() => { }}
						setdensityInConvertedUnit={() => { }}
						chargeableWeightInSelectedUnit={weightSummary.chargeableWeightInSelectedUnit}
						cageList={[]}
						shippingItem={shippingItem}
						AviEnabled={false}
						cages={[]}
						isSlrBooking
					/>
				</div>
			)}

			{/* SLR Booking Instructions */}
			<div className="new-booking-container">
				<div className="file-upload-block slr-instructions-block">
					<div className="article-show-section">
						<div className="slr-instructions-heading">
							<img src={errorFilledOrange} alt="alert-warn" title="Warning" className="alert-warn" />
							<h3>{t('BOOKING.SLR_INSTRUCTIONS_HEADING')}</h3>
						</div>
						<ul className="article-list">
							<li className="mb-3">{t('BOOKING.SLR_INSTRUCTION_1')}</li>
							<li className="mb-3">{t('BOOKING.SLR_INSTRUCTION_2')}</li>
							<li className="mb-3">{t('BOOKING.SLR_INSTRUCTION_3')}</li>
							<li className="mb-3">{t('BOOKING.SLR_INSTRUCTION_4')}</li>
							<li>{t('BOOKING.SLR_INSTRUCTION_5')}</li>
						</ul>
					</div>
				</div>
			</div>

			{(isSubmitting || isUploadingAttachments) && <LoadingIndicator />}
			<CustomToast displayData={notification} setDisplayData={setNotification} />
			<AlertMessage
				type='error'
				message={processErrorMessage}
				onClose={() => setProcessErrorMessage(null)}
				isPopup
			/>

			<div className='slr-footer-buttons'>
				<SecondaryButton
					label={t("COMMON.BACK_BUTTON")}
					onClick={() => handleBack()}
					disabled={isSubmitting || isUploadingAttachments}
				/>
				<PrimaryButton
					label={t("COMMON.CONFIRM")}
					onClick={() => handleConfirm()}
					disabled={isSubmitting || isUploadingAttachments}
				/>
			</div>

			{/* SLR Disqualify Popup */}
			{showDisqualifyPopup && (
				<MiniPopup
					type="Warning"
					mainMsg={t('BOOKING.SLR_DISQUALIFY_MSG')}
					subMsg={t('BOOKING.SLR_DISQUALIFY_SUB_MSG')}
					onConfirm={handleDisqualifyConfirm}
					onCancel={handleDisqualifyCancel}
				/>
			)}

			{/* SLR Result Popup */}
			<CustomPopup
				isVisible={showResultPopup}
				headerComponent={{
					mainHeader: resultData?.success
						? t('BOOKING.SLR_RESULT_SUCCESS_TITLE')
						: t('BOOKING.SLR_RESULT_ERROR_TITLE'),
				}}
				footerPriBtnLabel={t('COMMON.OK')}
				footerPriBtnClick={handleResultOk}
				onClose={() => setShowResultPopup(false)}
			>
				<div className="delete-confirm-msg-row">
					{resultData?.success ? (
						resultData?.slrNumber && (
							<div className="delete-confirm-msg mb-2">
								{t('BOOKING.SLR_RESULT_NUMBER')}: <span className="slr-bold">{resultData.slrNumber}.</span>
								{'  '}{t('BOOKING.SLR_RESULT_MESSAGE')}
							</div>
						)
					) : (
						<div className="delete-confirm-msg mb-2">
							{resultData?.errorMessage || t('BOOKING.SLR_RESULT_ERROR_SUBTITLE')}
						</div>
					)}
				</div>
			</CustomPopup>
		</div>
	)
}

export default SlrBooking
